import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  Divider,
} from "@mui/material";

import { useEffect, useState } from "react";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";

export default function ValidationRulesCard({ dataFormRef, triggerRefresh, formRules, rules, dataFormFields }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [tableRows, setTableRows] = useState([]);
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedRuleFields, setSelectedRuleFields] = useState({});
  const [openAddValidatorDialog, setOpenAddValidatorDialog] = useState(false);

  useEffect(() => {
    const rows = buildFormRulesRows(formRules);
    setTableRows(rows);
  }, [formRules]);

  async function removeValidator(ref) {
    setShowLoading(true);
    await axios
      .get(`portal/data_forms/validators/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const formRulesTableColumns = [
    { Header: "Rule", accessor: "title", align: "left" },
    { Header: "Fields", accessor: "fields", align: "left" },
    { Header: "Cost", accessor: "cost", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildFormRulesRows = (data) => {
    const rows = [];
    data.map((rule) => {
      rows.push({
        title: rule.title,
        fields: rule.fields,
        cost: rule.cost > 0 ? `${rule.currency}${(rule.cost / 100).toFixed(2)}` : `FREE TO USE`,
        actions: (
          <MDButton
            size="small"
            variant="outlined"
            color="error"
            iconOnly
            onClick={() => {
              if (window.confirm(`Delete this validator?`) === true) {
                removeValidator(rule.reference);
              }
            }}
          >
            <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
              delete
            </Icon>
          </MDButton>
        ),
      });
      return null;
    });

    return rows;
  };
  useEffect(() => {
    setSelectedRuleFields({});
  }, [selectedRule]);

  async function createValidationRule() {
    if (selectedRule === null) {
      setSeverity("error");
      setErrMsg("Please select a validation rule.");
      setOpen(true);
      return;
    }

    let errors = false;
    if (selectedRule.parameters.length > 0) {
      selectedRule.parameters.forEach((p) => {
        if (!selectedRuleFields[p.key]) {
          setSeverity("error");
          setErrMsg(`${p.display_name} is required.`);
          setOpen(true);
          errors = true;
        }
      });
    }

    if (errors) {
      return;
    }

    setOpenAddValidatorDialog(false);
    setShowLoading(true);

    const json = {
      form: dataFormRef,
      reference: selectedRule.reference,
      fields: selectedRuleFields,
    };

    console.log(json);

    await axios
      .post("portal/data_forms/validators/attach.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openAddValidatorDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Add Validation Rule</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox px={0} mb={5}>
            <MDBox mt={1}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Validation Rule</InputLabel>
                <Select
                  label="Validation Rule"
                  onChange={(e) => {
                    rules.forEach((rule) => {
                      if (rule.reference === e.target.value) {
                        setSelectedRule(rule);
                      }
                    });
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    rules.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      rules.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox>
              {selectedRule !== null && (
                <MDBox mt={2}>
                  <MDBox px={0.1}>
                    <Alert severity="info">
                      <MDTypography variant="subtitle2" fontSize={14}>
                        {selectedRule.description}
                      </MDTypography>
                      <MDTypography mt={1} variant="h6" fontSize={12} fontWeight="regular">
                        Cost:{" "}
                        {selectedRule.cost > 0
                          ? `${selectedRule.currency}${(selectedRule.cost / 100).toFixed(2)}`
                          : `FREE TO USE`}
                      </MDTypography>
                    </Alert>
                  </MDBox>
                  {selectedRule.parameters.length > 0 && (
                    <MDBox mt={3}>
                      <MDTypography pl={1} mb={2} variant="h6" fontSize={14} fontWeight="regular">
                        Map form fields to be validated
                      </MDTypography>
                      {selectedRule.parameters.map((param) => (
                        <MDBox mb={3}>
                          <FormControl sx={{ minWidth: 120, width: "100%" }}>
                            <InputLabel>{param.display_name}</InputLabel>
                            <Select
                              label={param.display_name}
                              onChange={(e) => {
                                const temp = selectedRuleFields;
                                temp[param.key] = e.target.value;
                                setSelectedRuleFields(temp);
                              }}
                              sx={{ padding: "12px", paddingLeft: "3px" }}
                            >
                              {
                                // --> Notice below changes
                                dataFormFields.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                                  dataFormFields.map((record) => (
                                    <MenuItem value={`${record.reference}`}>
                                      {record.display_name}
                                    </MenuItem>
                                  ))
                              }
                            </Select>
                          </FormControl>
                        </MDBox>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAddValidatorDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createValidationRule();
            }}
          >
            Add Rule
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox>
        <Card sx={{ width: "100%", marginTop: "10px" }}>
          <MDBox p={3} display="flex" justifyContent="space-between">
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Validation Rules
              </MDTypography>
            </MDBox>
            <MDBox>
              <MDButton
                variant="outlined"
                color="dark"
                iconOnly
                size="small"
                onClick={() => {
                  setSelectedRule(null);
                  setSelectedRuleFields({});
                  setOpenAddValidatorDialog(true);
                }}
              >
                <Icon color="dark">add</Icon>
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox>
            {formRules.length > 0 ? (
              <MDBox mt={1} pb={3}>
                <DataTable
                  table={{ columns: formRulesTableColumns, rows: tableRows }}
                  isSorted
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  canSearch={false}
                />
              </MDBox>
            ) : (
              <MDBox mt={1} align="center">
                <MDBox>
                  <MDTypography variant="h6" fontSize={48}>
                    <Icon>rules</Icon>
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                    Add validation rules from a wide selection that suits your data needs. Improve
                    overall data integrity.
                  </MDTypography>
                </MDBox>
                <MDBox mt={5} mb={3}>
                  <MDButton
                    variant="contained"
                    color="dark"
                    onClick={() => {
                      setSelectedRule(null);
                      setSelectedRuleFields({});
                      setOpenAddValidatorDialog(true);
                    }}
                    startIcon={<Icon>add</Icon>}
                  >
                    Add Validation Rule
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    </>
  );
}
